import { useMemo, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import { REIButton } from "../../components/ui";
import { DepreciationImage1, DepreciationImage2 } from "../../public/assets/images/depreciation";
import { httpPost } from "../../services/httpService";
import { KNOWLEDGE_BASE_URL, USER_DETAIL, WASHINGTON_BROWN_QUOTE_URL } from "../../utils/constants";
import { ApiEndPoints } from "../../utils/endpoints";
import WashingtonBrownCalculator from "./WashingtonBrownCalculator";
import { WBLogo } from "../../public/assets/images/depreciation";

const LEGACY_BG_IMAGES = {
  step2: DepreciationImage1,
  step1: DepreciationImage2,
};

const SUPPORT_SUBJECT = "Contact Support";

const Depreciation: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [showContactSupportModal, setShowContactSupportModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const memberDetail = useMemo(() => JSON.parse(localStorage.getItem(USER_DETAIL) || "{}"), []);
  const memberName = `${memberDetail.FirstName || ""} ${memberDetail.LastName || ""}`.trim();
  const [form, setForm] = useState({
    email: memberDetail.EmailAddress || "",
    message: "",
  });

  const openQuotePage = () => {
    window.open(WASHINGTON_BROWN_QUOTE_URL, "_blank", "noopener,noreferrer");
  };

  const openKnowledgeBase = () => {
    window.open(KNOWLEDGE_BASE_URL, "_blank", "noopener,noreferrer");
  };

  const openContactSupportModal = () => {
    setForm({
      email: memberDetail.EmailAddress || "",
      message: "",
    });
    setShowContactSupportModal(true);
  };

  const closeContactSupportModal = () => {
    setShowContactSupportModal(false);
    setForm({
      email: memberDetail.EmailAddress || "",
      message: "",
    });
  };

  const handleContactSupportSubmit = async () => {
    setLoading(true);

    try {
      const result = await httpPost(`${ApiEndPoints.rpData}/UpgradeRequestEmail`, {
        UserName: memberName,
        Email: form.email || memberDetail.EmailAddress || "",
        Message: form.message,
        ProductName: "Depreciation Report",
      });

      if (result.status === 200 && result.data !== null) {
        toast.current?.show({
          severity: "success",
          summary: "Contact Support",
          detail: "Our support team will contact you shortly.",
          life: 5000,
        });
        closeContactSupportModal();
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Contact Support",
          detail: result.error || "Unable to send your message right now.",
          life: 5000,
        });
      }
    } catch {
      toast.current?.show({
        severity: "error",
        summary: "Contact Support",
        detail: "Unable to send your message right now.",
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <div className="min-h-screen bg-white">
        <div className="w-full">
          <section className="overflow-hidden bg-white relative">
            <div className="h-1.5 bg-gradient-to-r from-cyan-400 via-[var(--primary-color)] to-cyan-400" />

            <div className="px-4 py-4 md:px-10 md:py-6 bg-gradient-to-b from-white to-slate-50 relative z-10">
              <div className="max-w-4xl mx-auto flex flex-col items-center text-center gap-3">
                <div className="max-w-[1200px] mx-auto text-center text-xs">
                  <img src={WBLogo} alt="Washington Brown" className="text-sm h-12" />
                </div>
                <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-black mb-2 mt-4 md:mb-3 md:mt-3 tracking-wide">
                  Tax Depreciation
                </h1>

                <p className="text-slate-600 text-base sm:text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
                  If you have bought an investment property, no matter the age, there might be an opportunity for you to
                  claim tax deductions based on depreciable aspects of the property.
                </p>
              </div>
            </div>
          </section>

          <section className="bg-gray-100 p-4 md:p-8">
            <div className="rounded-lg bg-gray-100 px-6 py-6 md:px-10 md:py-8 lg:py-10">
              <div className="flex flex-col bg-gray-100 lg:flex-row items-center justify-between gap-6">
                <div className="text-center lg:text-left">
                  <h3 className="text-xl md:text-3xl font-bold text-black  tracking-wide">Request A Quick Quote</h3>
                  <p className="text-slate-700 mt-2 text-sm md:text-lg max-w-xl">
                    Continue to receive your detailed depreciation schedule options.
                  </p>
                </div>
                <div className="shrink-0 w-full sm:w-auto">
                  <REIButton
                    id="dep-btn-quickquote"
                    label="GET A QUICK QUOTE NOW"
                    onClick={openQuotePage}
                    classNames="bg-[#238EA0] text-white hover:bg-cyan-50 border-none font-bold px-8 py-3 w-full sm:w-auto shadow-lg transition-all"
                  />
                </div>
              </div>
            </div>
          </section>
          <section className="bg-white border border-slate-200 shadow-lg p-4 md:p-8">
            <h2 className="text-xl md:text-[1.7rem] font-semibold text-black mb-3 md:mb-4 tracking-wide">
              What is a Depreciation Report?
            </h2>
            <div className="max-w-5xl space-y-3">
              <p className="text-slate-700 leading-6 md:leading-7 text-sm md:text-base">
                If you have an investment property and are not currently claiming depreciation, you're missing out.
                Depreciation means you can claim the hidden cost of wear and tear on your property against your taxable
                income. These deductions can lead to substantial tax returns.
              </p>
            </div>

            <div className="h-px bg-slate-200 my-5 md:my-6" />

            <h2 className="text-xl md:text-[1.7rem] font-semibold text-black mb-5 md:mb-6  tracking-wide">
              Key Benefits
            </h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 text-slate-700">
              {[
                "First of all - we guarantee you'll save at least twice our fee in the first 12 months - or your report will be free. So you have nothing to lose and plenty of tax deductions to gain.",
                "Washington Brown has offices throughout Australia, and, where necessary, undertakes to inspect your property within two weeks of the appointment being booked - a commitment that leads the industry in client service. Additionally, every property undergoes an initial assessment to determine whether an inspection is needed to maximise your deductions. If not, the depreciation report is completed without disturbing your tenants, and the cost saving is passed onto you.",
                "As appointees of major banks like Commonwealth, Suncorp and St. George, Washington Brown has access to real historical construction cost and planning data that only panel members can utilise.",
                "As industry leaders for more than 30 years, Washington Brown has an extensive client list including some of Australia’s largest developers, such as Meriton, Geocon & Gurner Group. This is testament to their expertise and ability to provide real tax savings.",
                "Washington Brown prepare more than 10,500 reports annually, delivering $1.5 billion in depreciation savings each year.",
                "A winning combination of accounting experience; construction industry knowledge; and a detailed understanding of property related tax law ensures you will receive the maximum tax deductions possible.",
              ].map((benefit) => (
                <li
                  key={benefit}
                  className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-slate-50/50 p-4 md:p-5 transition-all hover:border-emerald-200 hover:bg-emerald-50/30 group"
                >
                  <span className="inline-flex items-center justify-center h-8 w-8 rounded-full  bg-white border border-[#78daeb] text-[#238EA0] font-bold shrink-0 transition-transform group-hover:scale-110">
                    ✓
                  </span>
                  <span className="text-sm md:text-base leading-relaxed">{benefit}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="border border-slate-200 shadow-lg overflow-hidden bg-gray-100">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="p-4 md:p-10 bg-gradient-to-br from-white to-slate-50 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-5">
                  <span className="bg-[#238EA0] flex items-center justify-center w-10 h-10 rounded-full text-white font-bold shrink-0 shadow-md">
                    1
                  </span>
                  <h2 className="text-xl md:text-2xl font-semibold text-black  tracking-wide">
                    Estimate Your Tax Savings*
                  </h2>
                </div>

                <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm ring-1 ring-slate-100">
                  <WashingtonBrownCalculator></WashingtonBrownCalculator>
                </div>
                <p className="text-slate-700 leading-6 md:leading-7 mt-5 text-sm md:text-base">
                  We promise to save you at least twice our fee in the first full year or the report will be free. This
                  guarantee is based on the tax deduction calculated 12 months from your settlement date or the date the
                  property was first available for rent.
                </p>
                <p className="text-slate-500 text-xs md:text-[13px] mt-4 leading-5 italic">
                  *Disclaimer: Values shown in this summary are estimates only. All care has been taken to provide
                  figures that are as accurate as possible. Washington Brown will contact you to ensure your future tax
                  savings warrant the purchase of the depreciation report, or you will receive a full refund.
                </p>
              </div>
              <div
                className="min-h-[300px] md:min-h-[360px] lg:min-h-full bg-cover bg-center border-l border-slate-100"
                style={{ backgroundImage: `url(${LEGACY_BG_IMAGES.step1})` }}
              />
            </div>
          </section>

          <section className="border border-slate-200 shadow-lg overflow-hidden bg-gray-100">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div
                className="order-2 lg:order-1 min-h-[300px] md:min-h-[320px] lg:min-h-full bg-cover bg-center border-r border-slate-100"
                style={{ backgroundImage: `url(${LEGACY_BG_IMAGES.step2})` }}
              />
              <div className="order-1 lg:order-2 p-4 md:p-10 bg-gradient-to-br from-white to-slate-50 flex flex-col justify-center gap-5">
                <div className="flex items-center gap-3">
                  <span className="bg-[#238EA0] flex items-center justify-center w-10 h-10 rounded-full text-white font-bold shrink-0 shadow-md">
                    2
                  </span>
                  <h2 className="text-xl md:text-2xl font-semibold text-black tracking-wide">
                    Order Your Depreciation Report
                  </h2>
                </div>

                <div className="h-px bg-slate-200" />

                <p className="text-slate-700 leading-7 text-sm md:text-base">
                  Once we have this information our customer service team will promptly forward you a detailed
                  quotation, application and sample report via email.
                </p>

                <div className="flex flex-col gap-3">
                  <REIButton
                    id="dep-btn-freequote"
                    label="GET A FREE QUOTE NOW"
                    onClick={openQuotePage}
                    classNames="bg-[#238EA0] text-white hover:brightness-95 border-none font-bold px-10 py-3 w-full sm:w-auto shadow-md transition-all rounded-xl ring-2 ring-[var(--primary-color)]/20 hover:ring-[var(--primary-color)]/40 hover:shadow-lg active:scale-95"
                  />
                  <div className="flex items-center gap-2.5 text-orange-800">
                    <span className="flex shrink-0 items-center justify-center h-5 w-5 rounded-full bg-orange-200 text-orange-700 text-xs font-bold">
                      !
                    </span>
                    <p className="text-xs font-semibold leading-snug">
                      We guarantee you will save twice your fee or it's free!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="border border-slate-200 shadow-xl overflow-hidden bg-white">
            <div className="px-4 md:px-10 py-8 md:py-12 text-center bg-gradient-to-br from-slate-50 via-white to-cyan-50/30 relative">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-12 bg-gradient-to-b from-slate-200 to-transparent" />
              <h2 className="text-2xl md:text-3xl font-bold text-black  tracking-wide mb-4">Still Have Questions?</h2>
              <p className="text-slate-600 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-8">
                Explore our detailed knowledge base for comprehensive articles on depreciation, process walkthroughs,
                and tax compliance.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <REIButton
                  id="dep-btn-knowledgebase"
                  label="VISIT KNOWLEDGE BASE"
                  onClick={openKnowledgeBase}
                  classNames="bg-[#238EA0] hover:bg-slate-900 border-none px-10 py-3 w-full sm:w-auto shadow-md"
                />
                <REIButton
                  id="dep-btn-contactsupport"
                  label="CONTACT SUPPORT"
                  onClick={openContactSupportModal}
                  classNames="bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 px-10 py-3 w-full sm:w-auto shadow-sm"
                />
              </div>
            </div>
          </section>
        </div>

        <Dialog
          header="Contact Support"
          visible={showContactSupportModal}
          style={{ width: "90vw", maxWidth: "500px" }}
          onHide={closeContactSupportModal}
          draggable={false}
          className="rei-dialog"
        >
          <div className="flex flex-col gap-4 pt-2">
            <p className="text-sm text-slate-600 mb-2">
              Send us a message and our support team will get back to you shortly.
            </p>

            <div className="flex flex-col gap-2">
              <label htmlFor="contact-support-email" className="text-sm font-bold text-slate-700">
                Email Address
              </label>
              <InputText
                id="contact-support-email"
                data-testid="dep-txt-email"
                value={form.email}
                onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                placeholder="Enter your email"
                className="w-full"
                disabled
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="contact-support-subject" className="text-sm font-bold text-slate-700">
                Subject
              </label>
              <InputText
                id="contact-support-subject"
                data-testid="dep-txt-subject"
                value={SUPPORT_SUBJECT}
                disabled
                className="w-full bg-slate-50 text-slate-500"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="contact-support-message" className="text-sm font-bold text-slate-700">
                Message
              </label>
              <InputTextarea
                id="contact-support-message"
                data-testid="dep-txt-message"
                value={form.message}
                onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
                rows={4}
                placeholder="Hi, I need help with the depreciation report..."
                className="w-full resize-none"
              />
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <REIButton
                id="dep-btn-cancel"
                label="Cancel"
                onClick={closeContactSupportModal}
                classNames="bg-white text-slate-600 border border-slate-300"
              />
              <REIButton
                id="dep-btn-submit"
                label={loading ? "Sending..." : "Submit Enquiry"}
                onClick={handleContactSupportSubmit}
                classNames="bg-[var(--primary-color)] border-none"
                disabled={loading || !form.message.trim()}
              />
            </div>
          </div>
        </Dialog>
      </div>
    </>
  );
};

export default Depreciation;
